import React, { useState } from 'react';
import { Lock, GitMerge, SearchX, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { ui } from '../ui';

/**
 * Think-alone-then-share.
 *
 * The board and grid look exactly the same during this phase, students just
 * see only their own entries (they are stored under individual/$key, never in
 * the shared items node), and nobody can vote yet. The facilitator's screen
 * stays empty until they merge, which collapses duplicates, publishes the
 * class's combined list, reveals the missed findings as usual, and saves each
 * student their own personal gap list.
 */
export function StudentIndividualBanner({ view }) {
  return (
    <div className="bg-accent-tint rounded-card px-4 py-2.5 mb-section flex items-center gap-2.5">
      <Lock className="w-4 h-4 text-accent flex-shrink-0" />
      <p className="text-body text-ink truncate">
        <span className="font-medium">Individual phase.</span> Everything you add
        {view ? ` to ${view}` : ''} is visible to you alone; when the facilitator reveals, the class's entries are merged and you get your own list of what you missed.
      </p>
    </div>
  );
}

// Facilitator side: live submission stats + the merge trigger. One line, and
// Merge & share is the only filled control on screen while the phase is on.
export function FacilitatorIndividualBanner({ stats, onMerge }) {
  return (
    <div className="bg-accent-tint rounded-card px-4 py-2.5 mb-section flex items-center justify-between gap-3">
      <p className="text-body text-ink flex items-center gap-2.5 min-w-0">
        <Lock className="w-4 h-4 text-accent flex-shrink-0" />
        <span className="truncate">
          <span className="font-medium">Individual phase active</span>, students are working privately, so your board stays empty.
        </span>
        <span className="font-semibold whitespace-nowrap">
          {stats.students} student{stats.students === 1 ? '' : 's'} · {stats.entries} entr{stats.entries === 1 ? 'y' : 'ies'} so far
        </span>
      </p>
      <button
        onClick={onMerge}
        className={`${ui.btnPrimary} flex-shrink-0`}
        title="Collapse duplicates and publish everyone's entries to the shared board. Pressing Reveal on this step does the same."
      >
        <GitMerge className="w-4 h-4" />Merge &amp; share
      </button>
    </div>
  );
}

/**
 * Each student's own record of what they personally missed, written at merge
 * time and kept for the debrief. Two kinds of gap: case findings they never
 * elicited, and entries their classmates made that they didn't.
 */
export function PersonalGapsPanel({ gaps }) {
  const [open, setOpen] = useState(true);
  if (!gaps.length) return null;
  const total = gaps.reduce((a, g) => a + g.findings.length + g.peers.length, 0);

  return (
    <div className={`${ui.panel} mb-section`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-heading font-medium text-ink">
          <SearchX className="w-4 h-4 text-ink-2" />Your gaps this session ({total})
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-ink-2" /> : <ChevronDown className="w-4 h-4 text-ink-2" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3.5">
          <p className="text-meta text-ink-2">
            Saved from your own private entries, this is yours, not the class's.
          </p>
          {gaps.map((g) => (
            <div key={g.scope}>
              <h4 className={`${ui.label} mb-1.5`}>{g.label}</h4>
              {g.findings.length > 0 && (
                <ul className="space-y-1.5 mb-2">
                  {g.findings.map((f, i) => (
                    <li key={i} className="card bg-surface border-dashed border-flag-pending rounded-card px-3 py-2">
                      <span className="text-meta font-medium text-ink-2">{f.text}</span>
                      {f.detail && <span className="block text-body font-semibold text-ink mt-0.5">{f.detail}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {g.peers.length > 0 && (
                <p className="text-meta text-ink-2 leading-relaxed">
                  <Users className="w-3 h-3 inline-block mr-1 -mt-0.5 text-accent" />
                  Also listed by the class, not by you:{' '}
                  <span className="text-ink">
                    {g.peers.map((p) => `${p.text}${p.listedBy > 1 ? ` (${p.listedBy} listed)` : ''}`).join(' · ')}
                  </span>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
